import { CartGoalElement } from '@theme/cart-goal-base';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Powers the Sticky Cart Bar (`snippets/sticky-cart-bar.liquid`).
 *
 * Cart Goal math (progress, remaining amount, message) and cart data (item
 * count, subtotal) are computed server-side in Liquid and kept in sync via
 * the theme's existing Section Rendering API + morph pipeline — this
 * element is rendered as a sibling of `<theme-drawer id="cart-drawer">`
 * inside `snippets/cart-drawer.liquid`, so it's part of the exact same
 * morph target the drawer already uses. No new AJAX listener is added for
 * that data.
 *
 * On top of the shared `CartGoalElement` crossing-detection + celebration
 * (`@theme/cart-goal-base`), this class owns three things specific to the
 * Sticky Cart Bar: "smart visibility" (hidden while the cart is empty, the
 * Cart Drawer is open, or the page hasn't been scrolled past the hero —
 * matching design-reference/homepage.html's own `stickyCart` behaviour,
 * driven by the *existing* `theme-drawer:open`/`theme-drawer:close` events
 * already dispatched by `assets/theme-drawer.js`, not a new mechanism), the
 * scroll listener behind that last condition, and a brief shimmer on the
 * item-count/subtotal `<text-component>` values when they change (reusing
 * the theme's existing shimmer utility already used by the native cart —
 * see `assets/utilities.js`'s `TextComponent`).
 *
 * @extends {CartGoalElement}
 */
class StickyCartBar extends CartGoalElement {
  /** Matches design-reference/homepage.html's `window.scrollY > 500` reveal point. */
  static #SCROLL_THRESHOLD = 500;

  /** @type {boolean} */
  #drawerOpen = false;

  /** @type {boolean} */
  #scrolledPast = false;

  /** @type {number | null} */
  #scrollRaf = null;

  /**
   * At ≥990px the theme scrolls `.page-wrapper` internally (`overflow-y:
   * auto`, see assets/base.css) rather than the window/document — below
   * that breakpoint the window itself scrolls and `.page-wrapper` has no
   * overflow. `#currentScrollY()` checks both so this works at every width
   * without needing to duplicate the theme's own breakpoint.
   * @type {HTMLElement | null}
   */
  #pageWrapper = null;

  /** @type {{ itemCount: string, subtotal: string }} */
  #previousValues = { itemCount: '', subtotal: '' };

  connectedCallback() {
    super.connectedCallback();

    this.#pageWrapper = document.querySelector('.page-wrapper');
    this.#drawerOpen = document.getElementById('cart-drawer')?.hasAttribute('open') ?? false;
    this.#scrolledPast = this.#currentScrollY() > StickyCartBar.#SCROLL_THRESHOLD;
    this.#captureValues();
    this.#updateVisibility();

    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerClose);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#pageWrapper?.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerClose);
    window.removeEventListener('scroll', this.#onScroll);
    this.#pageWrapper?.removeEventListener('scroll', this.#onScroll);
    if (this.#scrollRaf !== null) cancelAnimationFrame(this.#scrollRaf);
  }

  /** @returns {number} */
  #currentScrollY() {
    return Math.max(window.scrollY, this.#pageWrapper?.scrollTop ?? 0);
  }

  #onScroll = () => {
    // Throttle to one visibility check per animation frame — cheap boolean
    // comparison, but avoids running it on every scroll event.
    if (this.#scrollRaf !== null) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      const scrolledPast = this.#currentScrollY() > StickyCartBar.#SCROLL_THRESHOLD;
      if (scrolledPast === this.#scrolledPast) return;
      this.#scrolledPast = scrolledPast;
      this.#updateVisibility();
    });
  };

  updatedCallback() {
    super.updatedCallback();
    this.#updateVisibility();
    this.#shimmerChangedValues();
  }

  /** @returns {number} */
  get itemCount() {
    return Number(this.getAttribute('item-count')) || 0;
  }

  /** @param {Event} event */
  #onDrawerOpen = (event) => {
    if (!this.#isCartDrawer(event)) return;
    this.#drawerOpen = true;
    this.#updateVisibility();
  };

  /** @param {Event} event */
  #onDrawerClose = (event) => {
    if (!this.#isCartDrawer(event)) return;
    this.#drawerOpen = false;
    this.#updateVisibility();
  };

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #isCartDrawer(event) {
    return event.target instanceof Element && event.target.id === 'cart-drawer';
  }

  #updateVisibility() {
    const visible = this.itemCount > 0 && !this.#drawerOpen && this.#scrolledPast;
    this.toggleAttribute('data-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
  }

  /**
   * Eligibility deliberately does *not* factor in scroll position: this bar
   * is the default owner of the celebration whenever the drawer is closed,
   * even if it hasn't scrolled into view yet at the exact crossing moment
   * (e.g. a quick-add from a product card while still at the top of the
   * page). Gating on scroll position too would mean a crossing that
   * happens while neither UI is on screen (drawer closed *and* not yet
   * scrolled) permanently misses the celebration for the session, since
   * the "already celebrated" flag is only ever set by whichever instance
   * actually claims it.
   * @returns {boolean}
   */
  isVisibleForCelebration() {
    return this.itemCount > 0 && !this.#drawerOpen;
  }

  onGoalReached() {
    const message = this.refs.message;
    if (message instanceof HTMLElement) {
      message.classList.remove('sticky-cart-bar__message--celebrate');
      void message.offsetWidth; // restart the animation
      message.classList.add('sticky-cart-bar__message--celebrate');
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const fill = this.refs.fill;
    if (fill instanceof HTMLElement) {
      fill.classList.remove('sticky-cart-bar__fill--glow');
      void fill.offsetWidth;
      fill.classList.add('sticky-cart-bar__fill--glow');

      const host = this.refs.particleHost;
      if (host instanceof HTMLElement) {
        this.spawnParticles(fill, host, 'sticky-cart-bar__particle');
      }
    }
  }

  #captureValues() {
    this.#previousValues = {
      itemCount: this.#refValue(this.refs.itemCount),
      subtotal: this.#refValue(this.refs.subtotal),
    };
  }

  #shimmerChangedValues() {
    const itemCountEl = this.refs.itemCount;
    const subtotalEl = this.refs.subtotal;

    const nextItemCount = this.#refValue(itemCountEl);
    const nextSubtotal = this.#refValue(subtotalEl);

    if (itemCountEl instanceof HTMLElement && nextItemCount !== this.#previousValues.itemCount) {
      this.#shimmer(itemCountEl);
    }
    if (subtotalEl instanceof HTMLElement && nextSubtotal !== this.#previousValues.subtotal) {
      this.#shimmer(subtotalEl);
    }

    this.#previousValues = { itemCount: nextItemCount, subtotal: nextSubtotal };
  }

  /**
   * @param {Element | Element[] | undefined} ref
   * @returns {string}
   */
  #refValue(ref) {
    return ref instanceof Element ? (ref.getAttribute('value') ?? '') : '';
  }

  /** @param {HTMLElement} el */
  #shimmer(el) {
    const shimmerable = /** @type {{ shimmer?: () => void }} */ (el);
    if (typeof shimmerable.shimmer !== 'function') return;
    shimmerable.shimmer();
    setTimeout(() => el.removeAttribute('shimmer'), 900);
  }
}

if (!customElements.get('sticky-cart-bar')) {
  customElements.define('sticky-cart-bar', StickyCartBar);
}
